<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t, locale } = useI18n()
import type {
  IAoeCart,
  ICartResponse,
  ICartPaymentMethodGetListItemParameters,
} from '@/types/CartTypes'
import {
  ExpansionPanel as KExpansionPanel,
  ExpansionPanelContent as KExpansionPanelContent,
} from '@progress/kendo-vue-layout'
import { Reveal as KReveal } from '@progress/kendo-vue-animation'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { Loader as KLoader } from '@progress/kendo-vue-indicators'
import { h, ref, computed, onMounted, defineComponent, createCommentVNode, onBeforeUnmount } from 'vue'
import BrainTree from './PaymentGateways/Braintree/BraintreePaymentEntry.vue'
import BraintreeApplePayPaymentEntry from './PaymentGateways/Braintree/BraintreeApplePayPaymentEntry.vue'
import BraintreeAPMPaymentEntry from './PaymentGateways/Braintree/BraintreeAPMPaymentEntry.vue'
import NotSupported from './PaymentGateways/NotSupported.vue'
import { useLogger } from 'vue-logger-plugin'
import { CartPaymentMethodGetList } from '@/api/Cart.ts'
import {
  DeletePaymentFromCart,
  SaveCartToLocalStorage,
} from '@/services/CartService'
import type { AoeMessageEvent, IErrorResponse } from '@/types/AoeTypes'
import { AlertUnknownError, AlertErrorResponse, handleError } from '@/services/ErrorService.ts'
import { SetIsLoading } from '@/services/AoeHelpers.ts'
import {SetupBraintree, BrainTreeControlFolderName, SetupBraintreeAPM, BrainTreeAPMControlFolderName} from '@/services/PaymentGateways/BraintreeService.ts'
import { FormatCurrency } from '@/services/currencyFormatter'

interface IBraintreePaymentCreatedEvent extends AoeMessageEvent {
  data: AoeMessageEvent['data'] & {
    paymentData?: {
      systemKeyword: string
      charge: number
      BraintreeHostedFieldTokenizeResponse?: {
        type?: string
        details?: Record<string, string>
      }
    }
  }
}

const props = defineProps<{
  cart: IAoeCart
}>()
const logger = useLogger()

logger.debug('AoeCheckoutPayment', 'props', props)

export interface iCheckoutPaymentViewModel {
  name: string
  systemKeyword: string
  controlFolderName: string
  expanded: boolean
  parameters?: ICartPaymentMethodGetListItemParameters
}
const viewModels = ref<Array<iCheckoutPaymentViewModel>>([])

const BrainTreeViewModel = ref<iCheckoutPaymentViewModel | null>(null)

const BraintreeApplePayMissingParamsComment = defineComponent({
  name: 'BraintreeApplePayMissingParamsComment',
  props: {
    braintreeMerchantId: { type: [String, null] as any, required: false },
    displayName: { type: [String, null] as any, required: false },
  },
  setup(props) {
    let hasLogged = false
    return () =>
      {
        const message = `BraintreeApplePayPaymentEntry not rendered: braintreeMerchantId=${String(
          (props as any).braintreeMerchantId,
        )}, displayName=${String((props as any).displayName)}`

        if (!hasLogged) {
          hasLogged = true
          logger.debug('AoeCheckoutPayment', 'BraintreeApplePayMissingParamsComment', message)
        }

        return createCommentVNode(message)
      }
  },
})



const expanded = computed(() => {
  const result: Array<boolean> = viewModels.value.map(panel => {
    return panel.expanded === true
  })
  return result
})

function expansionPanelHandler(index: number) {
  const currentValue = expanded.value[index]
  logger.debug('AoeCheckoutPayment', 'expansionPanelHandler', index, currentValue)

  if (currentValue == true) {
    viewModels.value[index].expanded = false
  } else {
    CloseAllPaymentOptions()
    viewModels.value[index].expanded = true
  }
  logger.debug('AoeCheckoutPayment', 'expansionPanelHandler', viewModels.value, expanded.value)
}

onMounted(async () => {
  logger.debug('AoeCheckoutPayment', 'onMounted', viewModels.value)
  if (props.cart.balance > 0)
  {
    SetIsLoading(true)
  await CartPaymentMethodGetList(props.cart.aoeCartIdToken)
    .then(async paymentMethods => {
      logger.debug(
        'AoeCheckoutPayment',
        'paymentMethods',
        paymentMethods,
        'cart.payments',
        props.cart.payments,
      )

      viewModels.value = paymentMethods.items.map(paymentMethod => {
        return {
          name: paymentMethod.name,
          systemKeyword: paymentMethod.systemKeyword,
          controlFolderName: paymentMethod.controlFolderName,
          expanded: false,
          parameters: paymentMethod.parameters,
        }
      })

      BrainTreeViewModel.value =
        viewModels.value.find(
          vm => vm.controlFolderName === BrainTreeControlFolderName,
        ) ?? null

      logger.debug(
        'AoeCheckoutPayment',
        'BrainTreeViewModel',
        BrainTreeViewModel.value?.systemKeyword ?? null,
        BrainTreeViewModel.value?.parameters?.BraintreeMerchantId ?? null,
        BrainTreeViewModel.value?.parameters?.DisplayName ?? null,
      )

      if (props.cart.balance > 0)
      {
        await SetupBraintree(t, props.cart, viewModels.value)
        await SetupBraintreeAPM(t, props.cart, viewModels.value)
      }

    })
    .catch(async error => {
      await handleError(error, t)
    })
    .finally(() => {
      SetIsLoading(false)
      window.addEventListener('message', messagehandler)
    })
  }
  else
  {
    window.addEventListener('message', messagehandler)
  }
})

async function deletePaymentClickedHandler(lineNumber: number) {
  logger.debug('AoeCheckoutPayment', 'deletePaymentClickedHandler', lineNumber)
  await DeletePaymentFromCart({
    aoeCartIdToken: props.cart.aoeCartIdToken,
    lineNumber,
  })
}

async function messagehandler(e: AoeMessageEvent) {
  logger.debug('AoeCheckoutPayment', 'messagehandler', e)
  const allowedOrigin = `${window.location.protocol}//${window.location.hostname}`
  if (!e.origin.startsWith(allowedOrigin)) {
    logger.debug(
      'AoeCheckoutPayment',
      'messagehandler',
      'origin not allowed',
      e.origin,
      allowedOrigin,
    )
    return
  }
  if (e.data && e.data.messageType === 'aoepaymentcreated') {
    logger.debug(
      'AoeCheckoutPayment',
      'messagehandler',
      'aoepaymentcreated',
      e.data.messageData,
    )
    const cartResponse = e.data.messageData as ICartResponse
    if (cartResponse?.item) {
      SaveCartToLocalStorage(cartResponse)
    } else {
      SaveCartToLocalStorage(createCartResponseWithPayment(e as IBraintreePaymentCreatedEvent))
    }
  }
  if (e.data && e.data.messageType === 'aoepaymentcreationerror') {
    logger.debug(
      'AoeCheckoutPayment',
      'messagehandler',
      'aoepaymentcreationerror',
      e.data.messageData,
    )
    CloseAllPaymentOptions()

    const result = await AlertErrorResponse(t, e.data.messageData as IErrorResponse)
    if (result) {
      return
    }
    AlertUnknownError(t)
  }
}

function createCartResponseWithPayment(e: IBraintreePaymentCreatedEvent): ICartResponse {
  const paymentData = e.data.paymentData
  const tokenizeResponse = paymentData?.BraintreeHostedFieldTokenizeResponse
  const charge = paymentData?.charge ?? 0
  const paymentTotal = (props.cart.paymentTotal ?? 0) + charge
  const currentPayments = props.cart.payments ?? []
  const nextLineNumber = currentPayments.length > 0
    ? Math.max(...currentPayments.map(payment => payment.lineNumber)) + 1
    : 1

  return {
    item: {
      ...props.cart,
      paymentTotal,
      balance: Math.max((props.cart.balance ?? 0) - charge, 0),
      payments: [
        ...currentPayments,
        {
          lineNumber: nextLineNumber,
          systemKeyword: paymentData?.systemKeyword ?? '',
          charge,
          allowsDelete: false,
          transactionStateText: 'Not Processed',
          attributes: {
            ...(tokenizeResponse?.details ?? {}),
            type: tokenizeResponse?.type ?? 'CreditCard',
          },
        },
      ],
    },
  } as ICartResponse
}

onBeforeUnmount(() => {
  window.removeEventListener('message', messagehandler)
})

function CloseAllPaymentOptions() {
  viewModels.value.forEach(element => {
    element.expanded = false
  })
}

function translatePaymentMethod(systemKeyword: string, paymentType: string): string {
   let translationKey = `checkout.paymentMethods.${systemKeyword}`
  if(paymentType){
    if(paymentType.toLowerCase() === 'paypalaccount'){
        translationKey = `checkout.paymentMethods.PayPal`
    }
  }
  if(paymentType){
    if(paymentType.toLowerCase() === 'eps'){
        translationKey = `checkout.paymentMethods.EPS`
    }
  }

  const translated = t(translationKey)
  // If translation key doesn't exist, t() returns the key itself
  return translated !== translationKey ? translated : systemKeyword
}

function translateTransactionState(transactionStateText: string): string {
  // Convert "Not Processed" to "NotProcessed" for key lookup
  const stateKey = transactionStateText.replace(/\s+/g, '')
  const translationKey = `checkout.transactionStates.${stateKey}`
  const translated = t(translationKey)
  // If translation key doesn't exist, return original text
  return translated !== translationKey ? translated : transactionStateText
}
</script>
<template>
  <div class="aoe-checkout-payment row mt-2">
    <div
      class="col-12"
      v-if="props.cart.payments && props.cart.payments.length > 0"
    >
      <div
        class="payment-row"
        v-for="payment in cart.payments"
        v-bind:key="payment.lineNumber"
      >
        <div class="payment-method">{{ translatePaymentMethod(payment.systemKeyword,payment.attributes.type) }}</div>
        <div class="payment-details">
          <span class="payment-charge">{{ FormatCurrency(payment.charge, null) }}</span>
          <span class="payment-status">{{ translateTransactionState(payment.transactionStateText) }}</span>
          <KButton
            v-if="payment.allowsDelete"
            :fill-mode="'clear'"
            @click="deletePaymentClickedHandler(payment.lineNumber)"
            class="deletePaymentButton"
            >&#x2715;</KButton
          >
        </div>
      </div>
    </div>
    <div class="col-12" v-if="props.cart.balance > 0">
      <div id="braintreepaypalbutton"></div>
      <div id="braintreepaypalbutton-placeholder" class="paypal-button-placeholder">
        <KLoader type="infinite-spinner" size="small" />
      </div>
      <div class="apple-pay-checkout-button-wrapper">
        <BraintreeApplePayPaymentEntry
          v-if="
            BrainTreeViewModel &&
            BrainTreeViewModel.parameters?.BraintreeMerchantId &&
            BrainTreeViewModel.parameters?.DisplayName
          "
          :cart="cart"
          :systemKeyword="BrainTreeViewModel.systemKeyword"
          :parameters="{
            braintreeMerchantId: BrainTreeViewModel.parameters!.BraintreeMerchantId!,
            displayName: BrainTreeViewModel.parameters!.DisplayName!,
          }"
        />

        <template v-else>
          <BraintreeApplePayMissingParamsComment
            :braintreeMerchantId="BrainTreeViewModel?.parameters?.BraintreeMerchantId ?? null"
            :displayName="BrainTreeViewModel?.parameters?.DisplayName ?? null"
          />
        </template>
      </div>
      <KExpansionPanel
        v-for="(viewModel, index) in viewModels"
        :title="viewModel.name"
        :title-render="() => h('button', { class: 'panel-header-button k-button k-button-md k-button-solid k-button-solid-primary' }, viewModel.name)"
        :expanded="expanded[index]"
        :tab-index="index"
        :key="index"
        @action="expansionPanelHandler(index)"
      >
        <KReveal :appear="expanded[index]">
          <KExpansionPanelContent v-if="expanded">
            <BrainTree
              v-if="viewModel.controlFolderName === BrainTreeControlFolderName"
              :cart="cart"
              :systemKeyword="viewModel.systemKeyword"
              :name="viewModel.name"
              :submitbuttonmessage="'checkout.CreditCartSubmit'"
              :check3DS="viewModel.parameters?.Check3DS === true || viewModel.parameters?.Check3DS === 'true'"
            ></BrainTree>

            <BraintreeAPMPaymentEntry
                v-else-if="viewModel.controlFolderName === BrainTreeAPMControlFolderName"
                :cart="cart"
                :systemKeyword="viewModel.systemKeyword"
                :name="viewModel.name"
              ></BraintreeAPMPaymentEntry>
            <NotSupported
              v-else
              :system-keyword="viewModel.systemKeyword"
              :name="viewModel.name"
            ></NotSupported>
          </KExpansionPanelContent>
        </KReveal>
      </KExpansionPanel>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.payment-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: $spacing-3 0;
  border-bottom: 1px solid #e0e0e0;
}

.payment-method {
  @include font-weight-medium;
  flex: 0 0 auto;
  margin-right: 12px;
}

.payment-details {
  display: flex;
  align-items: center;
  gap: $spacing-3;
  flex-wrap: nowrap;
  margin-left: auto;
}

.payment-charge {
  @include font-weight-semibold;
  white-space: nowrap;
}

.payment-status {
  @include typography-small;
  color: #666;
  white-space: nowrap;
}

.deletePaymentButton {
  min-width: 32px;
  height: 32px;
  @include typography-h4;
  padding: 0;
  color: #d32f2f;
  flex-shrink: 0;
}

.deletePaymentButton:hover {
  color: #b71c1c;
  background-color: rgba(#d32f2f, 0.1);
}

.aoe-checkout-payment {
  #braintreepaypalbutton,
  .apple-pay-checkout-button-wrapper {
    padding-top: $spacing-3;
    padding-right: $spacing-4;
    padding-bottom: $spacing-3;
    padding-left: $spacing-4;
  }

  :deep(.panel-header-button) {
    border: none;
    width: 100%;
    text-transform: uppercase;
    @include typography-body-large;
  }
}

/* Mobile responsive styles */
@media (max-width: 576px) {
  .payment-row {
    flex-direction: column;
    align-items: flex-start;
    padding: $spacing-4 0;
  }

  .payment-method {
    margin-bottom: 8px;
    margin-right: 0;
  }

  .payment-details {
    width: 100%;
    justify-content: space-between;
    margin-left: 0;
  }

  .payment-charge {
    @include typography-body-large;
  }

  .payment-status {
    @include typography-small;
  }
}

/* Tablet and small desktop */
@media (min-width: 577px) and (max-width: 768px) {
  .payment-details {
    gap: $gap-sm;
  }

  .payment-status {
    @include typography-small;
  }
}

</style>
